import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { IWizardStep } from 'app/core/components/wizard/interfaces';
import { focusFirst } from 'app/shared/lib/focus';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { AssessmentService } from 'app/core/services/assessment-service';
import { ExtendLocalState } from 'app/shared/models/local-state-extensions';
import { IAssessmentAttachmentOptions } from 'app/core/components/assessments/assessment-attachment';
import { IAssessmentsConfig, ITasksConfig } from 'app/core/models/essette-interfaces';
import { ICollapse } from 'app/shared/attributes/ess-collapse-attributes';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { AssessmentAttachmentCompletedReloadEvent } from 'app/core/lib/events';
import { AuthPortalPageContext } from 'app/pp/lib/auth-portal-page-context';
import { Toaster } from 'app/shared/lib/toaster';
import * as UM from 'app/um/models/essette-api';
import * as Core from 'app/core/models/essette-api';

type AttachmentWithLocalState = ExtendLocalState<Core.AssessmentAttachment, { isLoaded?: boolean; }>;

@autoinject()
export class AssessmentsStep extends ComponentBase implements IWizardStep {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.twoWay, changeHandler: 'reload' })
    protected auth: UM.Auth = {};

    @bindable.boolean()
    protected readonly: boolean = false;

    @bindable.objectNull({ defaultBindingMode: bindingMode.oneTime })
    public config: IAssessmentsConfig | null = null;

    @bindable.objectNull({ defaultBindingMode: bindingMode.oneTime })
    public tasksConfig: ITasksConfig | null = null;

    protected assessmentOptions: IAssessmentAttachmentOptions = {
        showActions: false,
        showBack: false,
        showHeader: false,
        showToc: false,
        showMember: false,
        showMemberSummaryLink: false,
        showTarget: false,
        showTargetSummaryLink: false,
        forceRequiredQuestions: true,
        canReopen: false,
        stickyActions: false,
        isTaskDisabled: false
    };

    private subscriptions: Array<Subscription> = [];

    protected attachments: ReadonlyArray<AttachmentWithLocalState> = [];

    protected ignoreExpandCollapse: boolean = false;
    protected allExpanded: boolean = true;
    protected allCollapsed: boolean = true;

    public canSkip: boolean = false;
    public canContinue: boolean = false;
    public canPerformFinalSubmit: boolean = false;

    ////****************** Element References ***************************************////
    protected $collapse!: ICollapse;
    protected $listElement!: HTMLElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        protected readonly context: AuthPortalPageContext,
        protected readonly env: EnvironmentService,
        private readonly assessmentService: AssessmentService,
        private readonly events: EventAggregator,
        private readonly toaster: Toaster,
        private readonly $element: Element,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        if(!this.config || !this.tasksConfig) {
            this.logger.warn('Assessment and/or tasks configs were not bound');
            return;
        }

        this.assessmentService.configure(this.config);

        this.subscriptions.push(
            this.events.subscribe(AssessmentAttachmentCompletedReloadEvent, (e: AssessmentAttachmentCompletedReloadEvent) => this.reload())
        );

        this.reload();
    }

    protected deactivate(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////
    public skipStep(): void { }
    public continueStep(): void {
        // show toast for user satisfaction though nothing new is actually saved when continuing from this step
        this.toaster.standard.saved('um:auth.$', true);
    }
    public openStep(): void {
        if(!this.readonly) setTimeout(() => focusFirst(this.$element), 250);
    }
    public async closeStep(): Promise<void> { }
    public async finalSubmit(): Promise<void> { }

    private async reload(): Promise<void> {
        if(!this.auth.id) return;
        await this.loadTargetAttachments(this.auth.id);

        this.canContinue = !!this.attachments.every(attachment => attachment.isCompleted);
    }

    protected collapseAll(event: Event): void {
        try {
            this.ignoreExpandCollapse = true;
            this.$collapse.closeAll();
            this.allCollapsed = true;
        }
        finally {
            this.ignoreExpandCollapse = false;
        }

        // remove focus from button
        if(event.target instanceof HTMLElement) { event.target.blur(); }
    }

    protected updateCollapsed(): void {
        if(!this.ignoreExpandCollapse) {
            const counts = this.$collapse.getCounts();
            this.allCollapsed = counts.closed === counts.total;
            this.allExpanded = counts.open === counts.total;
        }
    }

    ////****************** Service Calls ********************************************////
    private async loadTargetAttachments(targetId: string): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const includes: string[] = [
            'assessment',
            'lastSession',
            'lastSession.metadata.modified.byUser',
            'metadata.modified.byUser'
        ];

        try {
            this.state.beginLoad();
            const query = this.assessmentService.createAttachmentsByTargetQuery(targetId, options, includes);
            const response = await this.assessmentService.get<Core.AssessmentAttachment>(query);
            response.results.forEach((a: AttachmentWithLocalState) => {
                a._state = { isLoaded: false };
            });
            this.attachments = response.results;
            // if there are no attachments, they're all expanded
            this.allExpanded = !!this.attachments.length;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
